import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentOrgId } from '../auth';
import { useToast } from "../use-toast";
import { queryConfigs } from '@/lib/queryClient';

export interface QualityRule {
  field_key: string;
  field_label: string;
  enforcement: 'required' | 'suggested';
  validation_type: 'not_empty' | 'min_value';
  validation_value?: number;
  source?: 'deal' | 'lead';
}

export interface Stage {
  id: string;
  pipeline_id: string;
  name: string;
  stage_order: number;
  probability: number;
  focus_priority?: number;
  stage_type?: string | null;
  description?: string | null;
  quality_rules?: QualityRule[];
  quality_trigger?: 'on_enter' | 'on_exit' | 'both';
  created_at: string;
  updated_at: string;
}

export type PipelineProbabilitySource = 'stage' | 'deal';

export interface Pipeline {
  id: string;
  org_id: string;
  owner_id: string;
  name: string;
  is_active: boolean;
  is_default?: boolean;
  filter_id?: string;
  // 'stage' (default): Ponderado usa stages.probability; deal.probability é fallback.
  // 'deal': Ponderado usa deals.probability; stages.probability é fallback.
  probability_source: PipelineProbabilitySource;
  // Quando true, avançar pulando etapas exige as regras de qualidade das etapas
  // puladas (gate cumulativo, só para frente). Default false (comportamento legado).
  enforce_quality_on_skip?: boolean;
  created_at: string;
  updated_at: string;
  stages?: Stage[];
}

// Helper function to determine stage_type based on stage name
function getStageType(stageName: string): 'OPEN' | 'WON' | 'LOST' {
  const stageNameLower = stageName.toLowerCase().trim();
  
  if (stageNameLower === 'ganho' || stageNameLower === 'won' || stageNameLower === 'fechado' || stageNameLower === 'vendido') {
    return 'WON';
  } else if (stageNameLower === 'perdido' || stageNameLower === 'lost' || stageNameLower === 'descartado') {
    return 'LOST';
  }
  
  return 'OPEN';
}

export function usePipelines() {
  const { orgId } = useCurrentOrgId();

  return useQuery({
    ...queryConfigs.structural,
    queryKey: ['pipelines', orgId],
    queryFn: async () => {
      if (!orgId) throw new Error("Organization not found");

      const { data, error } = await supabase
        .from('pipelines')
        .select(`
          *,
          stages (
            id,
            pipeline_id,
            name,
            stage_order,
            probability,
            focus_priority,
            stage_type,
            description,
            quality_rules,
            quality_trigger,
            created_at,
            updated_at
          )
        `)
        .eq('org_id', orgId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as unknown as Pipeline[];
    },
    enabled: !!orgId
  });
}

export function usePipeline(pipelineId: string | undefined) {
  const { orgId } = useCurrentOrgId();

  return useQuery({
    ...queryConfigs.structural,
    queryKey: ['pipeline', pipelineId, orgId],
    queryFn: async (): Promise<Pipeline | null> => {
      if (!orgId || !pipelineId) return null;

      const { data, error } = await supabase
        .from('pipelines')
        .select(`
          *,
          stages (
            id,
            pipeline_id,
            name,
            stage_order,
            probability,
            focus_priority,
            stage_type,
            description,
            quality_rules,
            quality_trigger,
            created_at,
            updated_at
          )
        `)
        .eq('id', pipelineId)
        .eq('org_id', orgId)
        .maybeSingle();

      if (error) throw error;

      if (!data) return null;

      if (data.stages) {
        (data.stages as unknown as Stage[]).sort((a: Stage, b: Stage) => a.stage_order - b.stage_order);
      }

      return data as unknown as Pipeline;
    },
    enabled: !!orgId && !!pipelineId,
  });
}

export function useCreatePipeline() {
  const queryClient = useQueryClient();
  const { orgId } = useCurrentOrgId();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (pipelineData: { name: string; stages: { name: string; probability: number; description?: string }[]; probability_source?: PipelineProbabilitySource }) => {
      if (!orgId) throw new Error("Organization not found");

      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error("User not authenticated");

      // Create pipeline
      const { data: pipeline, error: pipelineError } = await supabase
        .from('pipelines')
        .insert({
          org_id: orgId,
          owner_id: user.user.id,
          name: pipelineData.name,
          is_active: true,
          ...(pipelineData.probability_source ? { probability_source: pipelineData.probability_source } : {}),
        })
        .select()
        .single();

      if (pipelineError) throw pipelineError;

      // Create stages with stage_type based on name
      const stages = pipelineData.stages.map((stage, index) => ({
        pipeline_id: pipeline.id,
        name: stage.name,
        stage_order: index + 1,
        probability: stage.probability,
        stage_type: getStageType(stage.name),
        description: stage.description || null
      }));

      const { error: stagesError } = await supabase
        .from('stages')
        .insert(stages);

      if (stagesError) throw stagesError;

      return pipeline;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipelines', orgId] });
      toast({
        title: "Pipeline criado!",
        description: "O pipeline foi criado com sucesso.",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao criar pipeline",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useUpdatePipeline() {
  const queryClient = useQueryClient();
  const { orgId } = useCurrentOrgId();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...pipelineData }: Partial<Pipeline> & { id: string }) => {
      const { data, error } = await supabase
        .from('pipelines')
        .update(pipelineData)
        .eq('id', id)
        .eq('org_id', orgId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['pipelines', orgId] });
      queryClient.invalidateQueries({ queryKey: ['pipeline', data.id] });
      toast({
        title: "Pipeline atualizado!",
        description: "O pipeline foi atualizado com sucesso.",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao atualizar pipeline",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

function useDeletePipeline() {
  const queryClient = useQueryClient();
  const { orgId } = useCurrentOrgId();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (pipelineId: string) => {
      const { error } = await supabase
        .from('pipelines')
        .delete()
        .eq('id', pipelineId)
        .eq('org_id', orgId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipelines', orgId] });
      toast({
        title: "Pipeline excluído!",
        description: "O pipeline foi excluído com sucesso.",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao excluir pipeline",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useCreateStage() {
  const queryClient = useQueryClient();
  const { orgId } = useCurrentOrgId();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (stageData: Omit<Stage, 'id' | 'created_at' | 'updated_at'>) => {
      // Automatically determine stage_type based on name if not provided
      const stageType = stageData.stage_type || getStageType(stageData.name);
      
      const { data, error } = await supabase
        .from('stages')
        .insert({
          ...stageData,
          stage_type: stageType
        } as never)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['pipeline', data.pipeline_id] });
      queryClient.invalidateQueries({ queryKey: ['pipelines', orgId] });
      toast({
        title: "Etapa criada!",
        description: "A etapa foi criada com sucesso.",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao criar etapa",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useUpdateStage() {
  const queryClient = useQueryClient();
  const { orgId } = useCurrentOrgId();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, pipeline_id, ...stageData }: Partial<Stage> & { id: string; pipeline_id: string }) => {
      // If name is being updated, automatically update stage_type based on new name
      const updateData = { ...stageData };
      if (updateData.name && !updateData.stage_type) {
        updateData.stage_type = getStageType(updateData.name);
      }
      
      const { data, error } = await supabase
        .from('stages')
        .update(updateData as never)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { data, pipeline_id };
    },
    onSuccess: ({ data, pipeline_id }) => {
      queryClient.invalidateQueries({ queryKey: ['pipeline', pipeline_id] });
      queryClient.invalidateQueries({ queryKey: ['pipelines', orgId] });
      toast({
        title: "Etapa atualizada!",
        description: "A etapa foi atualizada com sucesso.",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao atualizar etapa",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useDeleteStage() {
  const queryClient = useQueryClient();
  const { orgId } = useCurrentOrgId();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, pipeline_id }: { id: string; pipeline_id: string }) => {
      const { error } = await supabase
        .from('stages')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return pipeline_id;
    },
    onSuccess: (pipeline_id) => {
      queryClient.invalidateQueries({ queryKey: ['pipeline', pipeline_id] });
      queryClient.invalidateQueries({ queryKey: ['pipelines', orgId] });
      toast({
        title: "Etapa excluída!",
        description: "A etapa foi excluída com sucesso.",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao excluir etapa",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

// Initialize default pipeline for an organization
export function useInitializeDefaultPipeline() {
  const queryClient = useQueryClient();
  const { orgId } = useCurrentOrgId();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async () => {
      if (!orgId) throw new Error("Organization not found");

      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error("User not authenticated");

      const { data, error } = await supabase.rpc('create_default_sales_pipeline', {
        p_org_id: orgId,
        p_owner_id: user.user.id
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipelines', orgId] });
      toast({
        title: "Pipeline padrão criado!",
        description: "O pipeline de vendas foi criado com as etapas padrão.",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao criar pipeline padrão",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}
